import { P } from 'components'
import { capitalizeEveryWord, PMBFormatDate } from 'helpers'
import useIsMobile from 'hooks/useIsMobile'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { ArrowRight, ChevronDown, ChevronUp, Info, Package, RotateCcw } from 'react-feather'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Modal,
  ModalBody,
  Row,
  Spinner,
} from 'reactstrap'
import colors from 'Theme/colors'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_INVENTORY, URL_MUTATION_REPORT } from 'utils/pathUrl'
import { useFetchMutationReportQuery } from 'features/private-views/mutation-report/mutationReportSlice'
import { getAllInventory } from 'features/public-views/manageStock/API/manageStockAction'
import './style.css'


const isGroupNode = (node) =>
  Boolean(node?.groupLabel && Array.isArray(node?.items))


const flattenItems = (items) => {
  if (!Array.isArray(items)) return []
  const result = []
  for (const node of items) {
    if (isGroupNode(node)) {
      result.push(...flattenItems(node.items))
    } else {
      result.push(node)
    }
  }
  return result
}

const DetailOrderSection = ({
  transaction,
  setIsOpenSidebar,
  isAdjustment: isAdjustmentProp,
  isStatusAdjustmentApprovedOrRejected,
  transactionAdjustmentData,
  shouldHighlightAdjustedValues,
  shouldShowSellerAdjustmentWarning,
  isCreator,
  canOpenInventory = false,
}) => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [openingInventoryId, setOpeningInventoryId] = useState(null)
  const [isMissingInventoryOpen, setIsMissingInventoryOpen] = useState(false)

  // same lookup as LaporanKetelusuranSection (RTK cache shares the request); offered when the inventory is gone
  const transactionNumber =
    transaction?.orderDetail?.transaction_number?.trim() ||
    transaction?.orderDetail?.poNumber?.trim()
  const { data: traceData } = useFetchMutationReportQuery(
    { search: transactionNumber },
    { skip: !canOpenInventory || !transactionNumber },
  )
  const traceItem = traceData?.items?.[0]

  // Check the inventory still exists before leaving, so "Kembali" keeps the user on this transaction
  const handleOpenInventory = async (item) => {
    setOpeningInventoryId(item.inventoryId)
    const findIn = async (warehouseId) => {
      const res = await dispatch(
        getAllInventory({ search: item?.nameItem, all: true, warehouseId }),
      ).unwrap()
      return res?.items?.find(
        (row) => Number(row?.id) === Number(item.inventoryId),
      )
    }
    try {
      // PIC accounts only get their member warehouses without a warehouseId, so also check the null warehouse
      const row = (await findIn()) ?? (await findIn('null'))
      if (!row) {
        setIsMissingInventoryOpen(true)
        return
      }
      navigate(URL_INVENTORY, {
        state: { action: 'openInventory', inventoryId: item.inventoryId, row },
      })
    } catch (error) {
      enqueueSnackbar('Gagal memeriksa inventori.', { variant: 'error' })
    } finally {
      setOpeningInventoryId(null)
    }
  }

  const [expandedGroups, setExpandedGroups] = useState({})
  const [showAllAdjustedSummary, setShowAllAdjustedSummary] = useState(false)

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }))
  }

  const itemDetail = transaction?.itemDetail || []
  const allLeafItems = flattenItems(itemDetail)

  const getAdj = (item) => {
    const topAdjustments =
      transaction?.adjustments?.adjustmentitems ||
      transaction?.adjustment?.adjustmentitems ||
      transaction?.adjustmentitems
    if (Array.isArray(topAdjustments) && topAdjustments.length > 0) {
      const matched = topAdjustments.find(
        (adj) =>
          Number(adj.orderitemId) === Number(item?.orderitemId ?? item?.id) ||
          Number(adj.id) === Number(item?.orderitemId ?? item?.id) ||
          Number(adj.orderitemId) === Number(item?.id),
      )
      if (matched) return matched
    }

    return (
      item?.adjustmentitem ||
      item?.adjustmentItem ||
      (Array.isArray(item?.adjustmentitems) ? item.adjustmentitems[0] : item?.adjustmentitems)
    )
  }

  // Detect if any item has adjustment data embedded
  const hasAnyAdjustment = allLeafItems.some((item) => Boolean(getAdj(item)))

  // Use the prop if provided, otherwise detect from data
  const isAdjustment = isAdjustmentProp || hasAnyAdjustment

  const adjustedValueColor = shouldHighlightAdjustedValues
    ? '#f04438'
    : undefined
  const previousValueColor = '#667085'

  // Calculate transaction total
  const transactionSum = allLeafItems.reduce((sum, item) => {
    const adj = getAdj(item)
    if (isAdjustment && adj) {
      return sum + Number(adj.newTotalPrice ?? 0)
    }
    return sum + Number(item?.totalPrice ?? (item?.amount ?? 0) * (item?.price ?? 0))
  }, 0)

  // Calculate original total (before adjustment)
  const originalTotal = allLeafItems.reduce((sum, item) => {
    return sum + Number(item?.totalPrice ?? (item?.amount ?? 0) * (item?.price ?? 0))
  }, 0)

  // Unit summary
  const unitSummary = allLeafItems.reduce((acc, item) => {
    const unitRaw = item?.unit ?? ''
    const unit = String(unitRaw).toLowerCase()
    if (!unit) return acc

    if (!acc[unit]) {
      acc[unit] = {
        unit,
        inventoryCount: 0,
        totalAmount: 0,
        newTotalAmount: 0,
      }
    }

    acc[unit].inventoryCount += 1
    acc[unit].totalAmount += Number(item?.amount ?? 0)

    const adj = getAdj(item)
    if (isAdjustment && adj) {
      acc[unit].newTotalAmount += Number(adj.newAmount ?? 0)
    } else {
      acc[unit].newTotalAmount += Number(item?.amount ?? 0)
    }

    return acc
  }, {})

  const unitSummaryArray = Object.values(unitSummary || {})

  // List of items that have adjustments / differences
  const adjustedItemsList = allLeafItems.filter((item) => {
    const adj = getAdj(item)
    if (!isAdjustment || !adj) return false
    const origAmt = item?.amount
    const displayAmt = adj.newAmount
    const origPrice = item?.price
    const displayPrice = adj.newPrice
    const origTotal = item?.totalPrice ?? (origAmt ?? 0) * (origPrice ?? 0)
    const displayTotal = adj.newTotalPrice ?? (displayAmt ?? 0) * (displayPrice ?? 0)
    return (
      origAmt !== displayAmt ||
      origPrice !== displayPrice ||
      origTotal !== displayTotal
    )
  })

  // Calculate total price difference across all adjusted items
  const totalDiffAmount = adjustedItemsList.reduce((sum, item) => {
    const adj = getAdj(item)
    const origAmt = Number(item?.amount ?? 0)
    const origPrice = Number(item?.price ?? 0)
    const origTotal = Number(item?.totalPrice ?? origAmt * origPrice)
    const displayTotal = Number(
      adj?.newTotalPrice ?? Number(adj?.newAmount ?? 0) * Number(adj?.newPrice ?? 0)
    )
    return sum + (displayTotal - origTotal)
  }, 0)

  const renderLeafItem = (item, key) => {
    const adj = getAdj(item)
    const hasAdj = isAdjustment && Boolean(adj)

    const nameItem = item?.nameItem ?? '-'
    const unit = item?.unit ?? ''
    const inventoryOwnerCode = item?.inventoryOwnerCode
    const lotCode = item?.lotCode
    const bundle = item?.bundle

    const displayAmount = hasAdj ? adj.newAmount : item?.amount
    const displayPrice = hasAdj ? adj.newPrice : item?.price
    const displayTotal = hasAdj
      ? adj.newTotalPrice
      : (item?.totalPrice ?? (item?.amount ?? 0) * (item?.price ?? 0))

    const originalAmount = item?.amount
    const originalPrice = item?.price
    const originalTotalPrice = item?.totalPrice ?? (originalAmount ?? 0) * (originalPrice ?? 0)

    const showOriginal =
      hasAdj &&
      (originalAmount !== displayAmount ||
        originalPrice !== displayPrice ||
        originalTotalPrice !== displayTotal)

    const diffAmount = Number(displayAmount ?? 0) - Number(originalAmount ?? 0)
    const diffTotal = Number(displayTotal ?? 0) - Number(originalTotalPrice ?? 0)

    return (
      <div
        key={key}
        className="mb-2 bg-white p-2 rounded border border-gray-200"
        data-testid={`detail-transaction-order-item-${item?.orderitemId ?? key}`}
      >
        {/* Item name */}
        <div className="d-flex justify-content-between align-items-center mb-1">
          {canOpenInventory && item?.inventoryId ? (
            <Button
              color="link"
              className="p-0 fw-semibold text-start text-decoration-none d-flex align-items-center gap-1"
              title="Lihat di Inventori"
              disabled={openingInventoryId !== null}
              onClick={() => handleOpenInventory(item)}
              data-testid={`detail-transaction-open-inventory-${item.inventoryId}`}
            >
              {nameItem}
              {openingInventoryId === item.inventoryId && <Spinner size="sm" />}
            </Button>
          ) : (
            <span className="fw-semibold">{nameItem}</span>
          )}
          <div className="d-flex align-items-center gap-1">
            {bundle !== undefined && bundle !== null && Number(bundle) > 0 && (
              <span className="badge bg-primary">{bundle} bundle</span>
            )}
          </div>
        </div>

        {/* Amount x Price = Total (adjusted values) */}
        <div className="d-flex justify-content-between align-items-center">
          <P
            size="regular"
            className="fw-semibold mb-0"
            style={{ color: hasAdj ? adjustedValueColor : undefined }}
          >
            {newFormatThousandSeparator(Number(displayAmount ?? 0))}{' '}
            <span className="text-capitalize">{unit}</span>
            {' x Rp'}
            {newFormatThousandSeparator(Number(displayPrice ?? 0), 0)}
          </P>
          <P
            className={`fw-semibold mb-0 ${hasAdj && shouldHighlightAdjustedValues ? '' : 'text-primary'}`}
            style={{ color: hasAdj ? adjustedValueColor : undefined }}
          >
            Rp{newFormatThousandSeparator(Number(displayTotal ?? 0), 0)}
          </P>
        </div>

        {/* Original values (grey, strikethrough, explicitly labeled) */}
        {showOriginal && (
          <div
            className="d-flex justify-content-between align-items-center text-muted mt-1"
            style={{ fontSize: '0.8rem' }}
          >
            <span>
              <span
                className="badge bg-light text-secondary border me-1 fw-normal"
                style={{ fontSize: '0.68rem', padding: '2px 5px' }}
              >
                Semula
              </span>
              <span className="text-decoration-line-through">
                {newFormatThousandSeparator(Number(originalAmount ?? 0))}{' '}
                <span className="text-capitalize">{unit}</span>
                {' x Rp'}
                {newFormatThousandSeparator(Number(originalPrice ?? 0), 0)}
              </span>
            </span>
            <span className="text-decoration-line-through">
              Rp{newFormatThousandSeparator(Number(originalTotalPrice ?? 0), 0)}
            </span>
          </div>
        )}

        {/* Inventory code & Lot ID & Bundle */}
        <div
          className="text-muted mt-1 d-flex flex-column"
          style={{ fontSize: '0.8em', marginTop: '2px' }}
        >
          <span>Kode Inventori: {inventoryOwnerCode ?? '-'}</span>
          <span>Lot ID: {lotCode ?? '-'}</span>
          {(item?.projectLabel || item?.projectName || item?.projectId) && (
            <span>
              Project:{' '}
              {item?.projectLabel ||
                item?.projectName ||
                `Project #${item.projectId}`}
            </span>
          )}
        </div>

        {/* Callout Card for Adjustment / Return Info */}
        {hasAdj && Boolean(adj) && (
          <div
            className="mt-2 p-2 rounded"
            style={{
              backgroundColor: '#F9FAFB',
              border: '1px solid #EAECF0',
            }}
          >
            <div
              className="d-flex align-items-center justify-content-between mb-2 pb-1 border-bottom"
              style={{ borderColor: '#EAECF0' }}
            >
              <span
                className="fw-semibold text-dark d-flex align-items-center gap-1"
                style={{ fontSize: '0.75rem' }}
              >
                <RotateCcw size={13} className="text-warning" /> Detail Perubahan
              </span>
              <span
                className="badge"
                style={{
                  backgroundColor:
                    adj?.editType?.toUpperCase() === 'RETURN' ||
                      (!adj?.editType && (diffAmount < 0 || adj?.causeOfChange))
                      ? '#FEE4E2'
                      : '#FEF0C7',
                  color:
                    adj?.editType?.toUpperCase() === 'RETURN' ||
                      (!adj?.editType && (diffAmount < 0 || adj?.causeOfChange))
                      ? '#B42318'
                      : '#B54708',
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  padding: '3px 8px',
                  borderRadius: '12px',
                }}
              >
                {adj?.editType && adj?.editType !== ''
                  ? adj?.editType?.toUpperCase() === 'ADJUSTMENT'
                    ? 'Penyesuaian'
                    : capitalizeEveryWord(adj.editType)
                  : diffAmount < 0 || adj?.causeOfChange
                    ? 'Return'
                    : 'Penyesuaian'}
              </span>
            </div>

            <Row className="g-2" style={{ fontSize: '0.75rem' }}>
              <Col xs="6">
                <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                  Penyebab
                </span>
                <span className="fw-semibold text-dark">
                  {adj.causeOfChange || '-'}
                </span>
              </Col>
              <Col xs="6">
                <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                  Catatan
                </span>
                <span className="fw-semibold text-dark">
                  {adj.reason && adj.reason !== '' ? adj.reason : '-'}
                </span>
              </Col>
              {showOriginal && (
                <>
                  <Col xs="6" className="mt-1">
                    <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                      Selisih Kuantitas
                    </span>
                    <span className={`fw-semibold ${diffAmount < 0 ? 'text-danger' : diffAmount > 0 ? 'text-success' : 'text-dark'}`}>
                      {diffAmount < 0
                        ? `Diretur ${newFormatThousandSeparator(Math.abs(diffAmount))} ${unit}`
                        : diffAmount > 0
                          ? `+${newFormatThousandSeparator(diffAmount)} ${unit}`
                          : '0'}
                    </span>
                  </Col>
                  <Col xs="6" className="mt-1">
                    <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                      Selisih Harga Total
                    </span>
                    <span className={`fw-semibold ${diffTotal < 0 ? 'text-danger' : diffTotal > 0 ? 'text-success' : 'text-dark'}`}>
                      {diffTotal < 0
                        ? `-Rp${newFormatThousandSeparator(Math.abs(diffTotal), 0)}`
                        : diffTotal > 0
                          ? `+Rp${newFormatThousandSeparator(diffTotal, 0)}`
                          : 'Rp0'}
                    </span>
                  </Col>
                </>
              )}
              {adj?.checkTime && (
                <>
                  <Col xs="6" className="mt-1">
                    <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                      Diperiksa Oleh
                    </span>
                    <span className="text-dark">{adj?.checkUserName ?? '-'}</span>
                  </Col>
                  <Col xs="6" className="mt-1">
                    <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                      Waktu Perubahan
                    </span>
                    <span className="text-dark">
                      {PMBFormatDate(adj.checkTime)}
                    </span>
                  </Col>
                </>
              )}
            </Row>
          </div>
        )}
      </div>
    )
  }

  const renderGroupNode = (node, depth = 0, parentPath = '') => {
    const groupKey = `${parentPath}_${node.groupLabel}_${depth}`
    const isOpen = expandedGroups[groupKey] === true
    const groupLeafItems = flattenItems(node.items)
    const leafCount = groupLeafItems.length
    const groupTotal = groupLeafItems.reduce((sum, item) => {
      const adj = getAdj(item)
      if (isAdjustment && adj) {
        return sum + Number(adj.newTotalPrice ?? 0)
      }
      return sum + Number(item?.totalPrice ?? (item?.amount ?? 0) * (item?.price ?? 0))
    }, 0)

    // Calculate group per-unit breakdown
    const groupUnitSummary = groupLeafItems.reduce((acc, item) => {
      const unitRaw = item?.unit ?? ''
      const unit = String(unitRaw).trim().toLowerCase()
      if (!unit) return acc

      if (!acc[unit]) {
        acc[unit] = {
          unit: unitRaw,
          totalAmount: 0,
          newTotalAmount: 0,
        }
      }

      acc[unit].totalAmount += Number(item?.amount ?? 0)

      const adj = getAdj(item)
      if (isAdjustment && adj) {
        acc[unit].newTotalAmount += Number(adj.newAmount ?? 0)
      } else {
        acc[unit].newTotalAmount += Number(item?.amount ?? 0)
      }

      return acc
    }, {})

    const groupUnitTexts = Object.values(groupUnitSummary).map((u) => {
      const count = isAdjustment ? u.newTotalAmount : u.totalAmount
      return `${newFormatThousandSeparator(count, 3)} ${u.unit}`
    })

    const groupBundleCount = groupLeafItems.reduce(
      (sum, item) => sum + (Number(item?.bundle) || 0),
      0,
    )

    const groupMetaTexts = [`${leafCount} barang`]
    if (groupBundleCount > 0) {
      groupMetaTexts.push(`${newFormatThousandSeparator(groupBundleCount, 0)} bundle`)
    }

    const groupSummaryText =
      groupUnitTexts.length > 0
        ? `${groupUnitTexts.join(', ')} (${groupMetaTexts.join(' dan ')})`
        : `Terdiri dari ${groupMetaTexts.join(' dan ')}`

    return (
      <div
        key={groupKey}
        className={`mb-2 ${depth === 0 ? 'bg-light' : 'bg-white'} p-2 rounded border border-gray-200`}
        style={{ marginLeft: depth > 0 ? `${Math.min(depth * 8, 24)}px` : 0 }}
      >
        {/* Group header */}
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div className="d-flex align-items-center gap-2">
            <Package size={16} className="text-primary" />
            <span className="fw-bold">{node.groupLabel}</span>
          </div>

          <Button
            onClick={() => toggleGroup(groupKey)}
            className="btn btn-sm btn-link p-0 text-decoration-none d-flex align-items-center gap-1 btn-hover-none"
          >
            <span
              className="text-primary fw-medium"
              style={{ fontSize: '12px' }}
            >
              {isOpen ? 'Tutup' : `Lihat Isi (${leafCount})`}
            </span>
            {isOpen ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </Button>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
          <P size="small" color="gray" className="mb-0">
            {isOpen ? '' : groupSummaryText}
          </P>
          <P
            bold
            className={
              shouldHighlightAdjustedValues ? 'mb-0' : 'text-primary mb-0'
            }
            style={{ color: adjustedValueColor }}
          >
            Rp{newFormatThousandSeparator(Number(groupTotal), 0)}
          </P>
        </div>

        {/* Group content (collapsible) */}
        <Collapse isOpen={isOpen}>
          <div className="pt-2">
            {node.items.map((child, idx) => {
              const childPath = `${groupKey}_${idx}`
              if (isGroupNode(child)) {
                return renderGroupNode(child, depth + 1, childPath)
              }
              return renderLeafItem(child, childPath)
            })}
          </div>
        </Collapse>
      </div>
    )
  }


  return (
    <>
    <Modal
      className="p-4"
      centered
      isOpen={isMissingInventoryOpen}
      toggle={() => setIsMissingInventoryOpen(false)}
      autoFocus={false}
      data-testid="detail-transaction-modal-missing-inventory"
    >
      <ModalBody>
        <P size="medium" className="fw-semibold mb-1">
          Inventori tidak tersedia
        </P>
        <P size="regular" className="text-sm mb-4" color="#475467">
          Barang ini sudah tidak ada di stok Anda.
        </P>
        <div className="d-flex gap-3">
          <Button
            onClick={() => setIsMissingInventoryOpen(false)}
            className="btn-cancel"
            outline
            block
            data-testid="detail-transaction-modal-missing-inventory-back"
          >
            Kembali
          </Button>
          {traceItem?.id && (
            <Button
              color="primary"
              block
              onClick={() =>
                navigate(`${URL_MUTATION_REPORT}/${traceItem.id}`, {
                  state: { rowData: traceItem, direction: 'FORWARD' },
                })
              }
              data-testid="detail-transaction-modal-missing-inventory-trace"
            >
              Laporan Ketelusuran
            </Button>
          )}
        </div>
      </ModalBody>
    </Modal>
    <Card
      className="shadow-sm border-0 mb-3"
      style={{
        position: isMobile ? 'relative' : 'sticky',
        top: isMobile ? 'auto' : '20px',
      }}
    >
      <CardBody>
        <div className="d-flex justify-content-between align-items-center">
          <div className="mb-3">
            <P bold>Rincian Pesanan</P>
            {shouldHighlightAdjustedValues ? (
              <div className="d-flex align-items-center gap-2">
                <Info size={15} color="#d92d20" />
                <P bold size="small" color={'#d92d20'}>
                  Harga dan jumlah bertanda merah menunjukkan nilai setelah
                  penyesuaian
                </P>
              </div>
            ) : isAdjustment ? (
              <div className="d-flex align-items-center gap-2">
                <Info size={15} color="grey" />
                <P bold size="small" color={'grey'}>
                  Harga dan jumlah berwarna abu-abu menunjukkan nilai sebelum
                  penyesuaian
                </P>
              </div>
            ) : null}

            {shouldShowSellerAdjustmentWarning ? (
              <div
                className="d-flex align-items-center gap-2"
                style={{
                  backgroundColor: '#fee4e2',
                  padding: 10,
                  borderRadius: 8,
                }}
              >
                <Info size={15} color="#d92d20" />
                <P bold size="small" color={'#d92d20'}>
                  Transaksi ini memerlukan penyesuaian harga, jika tidak maka
                  angka yang berwarna merah akan tetap berlaku.
                </P>
              </div>
            ) : null}
          </div>
        </div>

        {/* SUMMARY CARD FOR RETURNED / ADJUSTED ITEMS */}
        {adjustedItemsList.length > 0 && (
          <div
            className="mb-3 p-3 rounded border"
            style={{
              backgroundColor: '#FFFAEB',
              borderColor: '#FEDF89',
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom"
              style={{ borderColor: '#FEF0C7' }}
            >
              <div className="d-flex align-items-center gap-2">
                <RotateCcw size={16} style={{ color: '#B54708' }} />
                <span
                  className="fw-bold"
                  style={{ color: '#B54708', fontSize: '0.85rem' }}
                >
                  Ringkasan Retur & Penyesuaian ({adjustedItemsList.length} Barang)
                </span>
              </div>
              <span
                className="fw-bold"
                style={{
                  color: totalDiffAmount < 0 ? '#B42318' : '#027948',
                  fontSize: '0.85rem',
                }}
              >
                Total Selisih:{' '}
                {totalDiffAmount < 0
                  ? `-Rp${newFormatThousandSeparator(Math.abs(totalDiffAmount), 0)}`
                  : `+Rp${newFormatThousandSeparator(totalDiffAmount, 0)}`}
              </span>
            </div>

            <div
              className="d-flex flex-column gap-2"
              style={{
                fontSize: '0.8rem',
                maxHeight: showAllAdjustedSummary ? '320px' : 'none',
                overflowY: showAllAdjustedSummary ? 'auto' : 'visible',
                paddingRight: showAllAdjustedSummary ? '4px' : '0px',
              }}
            >
              {(showAllAdjustedSummary
                ? adjustedItemsList
                : adjustedItemsList.slice(0, 3)
              ).map((item, idx) => {
                const adj = getAdj(item)
                const unit = item?.unit ?? ''
                const origAmt = Number(item?.amount ?? 0)
                const newAmt = Number(adj?.newAmount ?? origAmt)
                const diffAmt = newAmt - origAmt
                const origTot = Number(
                  item?.totalPrice ?? origAmt * Number(item?.price ?? 0),
                )
                const newTot = Number(
                  adj?.newTotalPrice ?? newAmt * Number(adj?.newPrice ?? 0),
                )
                const diffTot = newTot - origTot

                return (
                  <div
                    key={idx}
                    className="d-flex justify-content-between align-items-center bg-white p-2 rounded border"
                    style={{ borderColor: '#EAECF0' }}
                  >
                    <div>
                      <span
                        className="fw-semibold text-dark"
                        style={{ fontSize: '0.8rem' }}
                      >
                        {item?.nameItem ?? '-'}
                      </span>
                      <div
                        className="text-muted"
                        style={{ fontSize: '0.75rem' }}
                      >
                        Kuantitas: {newFormatThousandSeparator(origAmt)} {unit}{' '}
                        ➔ {newFormatThousandSeparator(newAmt)} {unit}
                      </div>
                    </div>
                    <div className="text-end">
                      {diffAmt !== 0 && (
                        <span
                          className={`badge ${diffAmt < 0 ? 'bg-danger-subtle text-danger border border-danger-subtle' : 'bg-success-subtle text-success border border-success-subtle'} me-1 fw-semibold`}
                          style={{ fontSize: '0.7rem' }}
                        >
                          {diffAmt < 0
                            ? `Diretur ${newFormatThousandSeparator(Math.abs(diffAmt))} ${unit}`
                            : `+${newFormatThousandSeparator(diffAmt)} ${unit}`}
                        </span>
                      )}
                      <div
                        className={`fw-semibold ${diffTot < 0 ? 'text-danger' : diffTot > 0 ? 'text-success' : 'text-dark'}`}
                        style={{ fontSize: '0.8rem' }}
                      >
                        {diffTot < 0
                          ? `-Rp${newFormatThousandSeparator(Math.abs(diffTot), 0)}`
                          : `+Rp${newFormatThousandSeparator(diffTot, 0)}`}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {adjustedItemsList.length > 3 && (
              <div
                className="text-center mt-2 pt-2 border-top"
                style={{ borderColor: '#FEF0C7' }}
              >
                <Button
                  color="link"
                  size="sm"
                  className="p-0 text-decoration-none fw-semibold"
                  style={{ color: '#B54708', fontSize: '0.78rem' }}
                  onClick={() => setShowAllAdjustedSummary((prev) => !prev)}
                >
                  {showAllAdjustedSummary
                    ? 'Tampilkan Lebih Sedikit ▲'
                    : `Lihat Selengkapnya (${adjustedItemsList.length - 3} barang lagi) ▼`}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* RENDER ITEM DETAIL (recursive groups + leaf items) */}
        {itemDetail.map((node, idx) => {
          if (isGroupNode(node)) {
            return renderGroupNode(node, 0, `root_${idx}`)
          }
          return renderLeafItem(node, `item_${idx}`)
        })}

        <hr />
        <div className="d-flex justify-content-between">
          <P bold size="medium">
            Total
          </P>
          <strong className="float-end">
            {isAdjustment ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <P
                    bold
                    size="medium"
                    style={{ color: adjustedValueColor }}
                    className={
                      shouldHighlightAdjustedValues
                        ? ''
                        : 'text-primary fs-5 mb-0'
                    }
                  >
                    Rp
                    {newFormatThousandSeparator(
                      Number(
                        transactionAdjustmentData?.adjustment?.newAmount ?? transactionSum ?? 0,
                      ),
                      0,
                    )}
                  </P>
                </div>
                {transactionAdjustmentData?.adjustment?.newAmount && transactionAdjustmentData?.adjustment?.newAmount !== originalTotal ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <P
                      style={{
                        color: previousValueColor,
                      }}
                    >
                      (Rp
                      {newFormatThousandSeparator(Number(originalTotal), 0)})
                    </P>
                  </div>
                ) : null}
              </>
            ) : (
              <P bold className="text-primary">
                Rp{newFormatThousandSeparator(Number(transactionSum ?? 0), 0)}
              </P>
            )}
          </strong>
        </div>

        {allLeafItems?.length > 1 &&
          (() => {
            const totalAmountSum = allLeafItems.reduce((sum, item) => {
              const adj = getAdj(item)
              const amt = isAdjustment && adj ? Number(adj.newAmount ?? 0) : Number(item?.amount ?? 0)
              return sum + amt
            }, 0)

            const uniqueUnits = Array.from(
              new Set(
                allLeafItems
                  .map((i) => String(i?.unit || '').trim().toLowerCase())
                  .filter(Boolean),
              ),
            )

            const isMonoUnit = uniqueUnits.length === 1
            const monoUnitName = allLeafItems.find((i) => Boolean(i?.unit?.trim()))?.unit || 'qty'
            const displayUnitLabel = isMonoUnit ? monoUnitName : 'qty'

            const uniqueCommodities = new Set(
              allLeafItems
                .map((i) => String(i?.nameItem || '').trim().toLowerCase())
                .filter(Boolean),
            )
            const totalCommoditiesCount = uniqueCommodities.size || allLeafItems.length

            const totalBundle = allLeafItems.reduce(
              (sum, item) => sum + (Number(item?.bundle) || 0),
              0,
            )

            const metaTexts = [`${totalCommoditiesCount} jenis barang`]
            if (totalBundle > 0) {
              metaTexts.push(`${newFormatThousandSeparator(totalBundle, 0)} bundle`)
            }

            const formattedTotalAmount = newFormatThousandSeparator(totalAmountSum, 3)
            const metaText = metaTexts.join(', ')

            return (
              <div
                className="mt-3 rounded"
                style={{
                  padding: '12px 16px',
                  backgroundColor: colors.primary[400],
                  color: 'white',
                }}
              >
                <P bold style={{ color: 'white', marginBottom: 0 }}>
                  {`Total: ${formattedTotalAmount} ${displayUnitLabel} (${metaText})`}
                </P>
              </div>
            )
          })()}
      </CardBody>
    </Card>
    </>
  )
}

export default DetailOrderSection
